import React, {useEffect, useState} from "react";
import {makeStyles} from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import {ErrorScreen, HeaderToolbar, Loading} from "../../components";
import Toolbar from "@material-ui/core/Toolbar";
import {useAuthUser} from "../../components/auth-provider";
import {SkuService} from "../../services";
import FuseAnimate from "../../components/fuse-animate/fuse-animate";
import Card from "@material-ui/core/Card";
import Typography from "@material-ui/core/Typography";
import ausPhone from "../../utils/aus-phone";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Button from "@material-ui/core/Button";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import {Checkbox} from "@material-ui/core";
import useStore from "../../store/hooks";
import {fetchAccount, invalidateAccount} from "../../store/actions";
import Checkout from "../../components/checkout/checkout";
import {CheckCircleOutline, SentimentVeryDissatisfied} from "@material-ui/icons";
import externalLink from "../../utils/external-link";
import {Link} from "react-router-dom";
import {totals} from "../../utils/checkout";

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        marginTop: 16,
        justifyContent: "center",
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        padding: 24,
        width: '100%',
        maxWidth: 340,
        [theme.breakpoints.up('sm')]: {
            maxWidth: 360,
        },
    },
    skuButton: {
        textTransform: 'none',
        padding: 0,
    }
}));

const RechargeServicePage = ({location, match}) => {
    const classes = useStyles();
    const serviceId = match.params.id;
    //console.log('serviceId', serviceId)

    const [store, dispatch] = useStore()
    const {user} = useAuthUser()

    const [state, setState] = useState({
        view: 'start',          // current view
        sku: {
            id: '',
            name: '',
            type: '',
            taxRate: 0,
            taxInc: false,
            price: 0,
            meta: {
                description: '',
                inclusions: {
                    minutes: {
                        quantity: 0,
                        expiryDays: 0,
                    },
                }
            }
        },
        skus: [],
        service: {id:0, number:'', callCredits:0 },
        purchase: {},
        order: {},
        termsAccepted: false,
        isFetching: false,
        error: null
    });

    // Scroll to top on mount
    useEffect( () => {
        document.getElementById('psb').scrollTo(0,0);
    }, [])  // <- empty deps means it runs only on load

    // Make sure the account is available in the store
    useEffect(()=> {
        dispatch(fetchAccount(user.accountId, store, dispatch))
    }, [user.accountId, store, dispatch])

    // Load SKUs on mount
    useEffect(() => {
        setState(state => ({...state, isFetching: true}));
        SkuService.getSkus()
            .then(skus => {
                setState(state => ({...state, skus, isFetching: false}));
            })
            .catch(error => {
                setState(state => ({...state, error, isFetching: false}));
            })
    }, []) // <- load on start

    // Set the selected service
    useEffect(() => {
        const services = store.account.services.filter(s => s.id.toString() === serviceId);
        if (services.length > 0) {
            setState(state => ({...state, service: services[0]}));
        }
    }, [serviceId, state.service, store.account])

    //console.log('state', state);

    return (
        <div id="add-service" className={classes.root}>
            <AppBar position="fixed" className={classes.appBar}>
                <HeaderToolbar location={location.pathname}/>
            </AppBar>
            <Toolbar/>

            <main className={classes.content}>
                { state.isFetching || store.account.isFetching ? <Loading/> : null }
                { store.account.error && <ErrorScreen error={store.account.error}/> }
                { state.error && <ErrorScreen error={state.error}/> }
                { state.error === null && !state.isFetching && store.account.error === null && !store.account.isFetching ?
                    <ContentCard state={state} setState={setState} />
                    : null
                }
            </main>

        </div>
    );
}

const ContentCard = ({state, setState}) => {
    const classes = useStyles();

    return (
        <FuseAnimate animation="transition.expandIn" delay={100}>
            <div id="recharge" className="flex flex-grow flex-col items-center">
                <Card className={classes.card}>
                    { state.view !== 'checkout' ?
                        <>
                            <Typography variant="h4" color="secondary">Recharge</Typography>
                            <Typography variant="h6" color="secondary">
                                <span className="whitespace-nowrap">{ausPhone(state.service.number)}</span>
                            </Typography>
                        </>
                        : null
                    }
                    { state.view === 'start' ?
                        <SkuList state={state} setState={setState} />
                        : null
                    }
                    { state.view === 'terms' ?
                        <TermsCard state={state} setState={setState} />
                        : null
                    }
                    { state.view === 'checkout' ?
                        <CheckoutCard state={state} setState={setState} />
                        : null
                    }
                    { state.view === 'success' ?
                        <SuccessPanel state={state} setState={setState} />
                        : null
                    }
                    {state.view === 'error' ?
                        <ApiErrorPanel error={state.error}/>
                        :null
                    }
                </Card>
            </div>
        </FuseAnimate>

    )
}

const SkuList = ({state, setState}) => {
    const classes = useStyles();

    const handleClick = (sku) => {
        //console.log('sku', sku)
        setState({...state, sku, view: 'terms'})
    }

    const getSize = (name) => {
        // Assumes the name is in the form "<description> - <size>"
        const idx = name.indexOf(' - ')
        if (idx === -1) {
            return '';
        }
        return name.substr(name.indexOf(' - ')+3);
    }

    return (
        <>
            <div className="mt-4">
                {/*<Typography variant="body1">Choose a recharge size</Typography>*/}
                <div className="uppercase text-lg font-semibold">Choose a recharge size</div>
            </div>
            <List>

            { state.skus.map((sku) => (
                <ListItem key={sku.id} component={Button} className={classes.skuButton} onClick={() => {handleClick(sku)}}>
                    <div key={sku.id} className="flex flex-row flex-grow rounded border-gray-200 border items-center p-4 my-2">
                        <Typography color="secondary" variant="h4">{getSize(sku.name)}</Typography>
                        <div className="flex flex-col flex-grow items-end">
                            <Typography variant="body1" align="right">
                                {sku.meta.inclusions.minutes.quantity} minutes
                                for <strong>${(sku.price/100).toFixed(0)}</strong>
                            </Typography>
                            <Typography variant="body1" color='textSecondary' >{(sku.price/sku.meta.inclusions.minutes.quantity).toFixed(1)}c per minute</Typography>
                        </div>

                    </div>
                </ListItem>
            ))}
            </List>
        </>
    )

}

const TermsCard = ({state, setState}) => {
    const minutes = state.sku.meta.inclusions.minutes;
    const price = (state.sku.price/100).toFixed(0)
    const number = state.service.number;
    const remaining = state.service.callCredits;

    const handleCheck = (e) => {
        setState({...state, termsAccepted: e.target.checked})
    }

    const handleClick = () => {
        setState({...state, view:'checkout'})
    }

    const handleBackClick = () => {
        setState({...state, view:'start'})
    }

    return (
        <FuseAnimate animation="transition.expandIn" delay={100}>
            <div className="flex flex-grow flex-col items-center mt-4">
                {/*<Card className={classes.card}>*/}
                    <div className="uppercase text-lg font-semibold">HERE'S WHAT YOU'RE GETTING</div>

                    <div className="w-full py-4">
                        <ul className="list-disc mx-4">
                            <li className="pb-3">
                                <strong>{minutes.quantity} call minutes</strong> for <strong>${price}</strong>
                            </li>
                                { state.service.callCredits > 0 ?
                                    <li className="pb-3">Your remaining <strong>{remaining} minutes</strong> will be rolled over
                                        to give you a total of <strong>{remaining+minutes.quantity} minutes</strong> to use in the
                                        next <strong>{minutes.expiryDays} days</strong>.
                                    </li>
                                    : null
                                }
                            <li className="pb-3">Unused call minutes will expire
                                in <strong>{minutes.expiryDays}&nbsp;days</strong> unless you recharge before.
                            </li>
                            <li className="pb-3">
                                Call minutes can only be used for calls
                                to <span className="font-bold whitespace-nowrap">{ausPhone(number)}</span> and are
                                not refundable.
                            </li>
                        </ul>

                        <FormControlLabel
                            label="Ok, got it!"
                            className="mt-2"
                            control={
                                <Checkbox name="checkTerms" color="primary" onChange={handleCheck}/>
                            }
                        />
                    </div>


                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleClick}
                        disabled={!state.termsAccepted}
                        className="w-full"
                    >
                        Proceed to checkout
                    </Button>

                <div className="w-full mt-4">
                    <Button onClick={handleBackClick} className="w-full"> Back </Button>
                </div>
            </div>
        </FuseAnimate>
    )

}

const CheckoutCard = ({state, setState}) => {

    const {subtotal, taxes, total} = totals(state.sku);
    // let subtotal = 0;
    // let taxes = 0;
    // let total = 0;
    //
    // if (state.sku.taxInc) {
    //     total = state.sku.price/100;
    //     taxes = total - (total/((100+state.sku.taxRate)/100))
    //     subtotal = total - taxes;
    // } else {
    //     subtotal = state.sku.price/100
    //     taxes = Math.round(state.sku.taxRate * state.sku.price/100)/100
    //     total = subtotal + taxes
    // }

    const order = {
        sku: state.sku,
        serviceId: state.service.id,
        number: {number: state.service.number, zone: state.service.meta.zone},
        subtotal: subtotal,
        taxes: taxes,
        total: total,
    }

    // Get the global dispatch function
    const [, dispatch] = useStore();

    const onSuccess = (purchase) => {
        // the checkout completed successfully
        //console.log('checkout succeeded', purchase);
        setState({...state, purchase, order, view: 'success'})

        // Invalidate the account in global store so we retrieve
        // the new service, dialActions, account status and paymentMethod if saved.
        dispatch(invalidateAccount())
    }

    const onError = (error) => {
        // the checkout failed
        //console.log('checkout failed', error);

        // display error panel
        setState({...state, order, error, view:'error'})
    }

    // The checkout card basically wraps the
    // Checkout component, injecting the SKU to be charged.
    return (
        <FuseAnimate animation="transition.expandIn" delay={100}>
            <div className="flex flex-grow flex-col items-center mt-4">
                <Checkout
                    order={order}
                    onSuccess={onSuccess}
                    onError={onError}
                />
            </div>
        </FuseAnimate>
    )
}

const SuccessPanel = ({state, setState}) => {

    // Scroll to top on mount
    useEffect( () => {
        document.getElementById('psb').scrollTo(0,0);
    }, [])  // <- empty deps means it runs only on load

    const number = state.purchase.recharge.number;
    const minutes = state.purchase.recharge.credits;
    const expiryDays = state.purchase.recharge.expiryDays;
    const receiptUrl = state.purchase.purchase.receiptUrl;
    const totalMinutes = state.purchase.recharge.closingCredits

    //console.log('receiptUrl', receiptUrl);
    //console.log('*** state', state);

    return (
        <FuseAnimate animation="transition.expandIn" delay={100}>
            <div className="flex flex-grow flex-col items-center">
                {/*<Card className={classes.card}>*/}
                    <FuseAnimate animation="callout.bounce" delay={400}>
                        <div className="flex flex-col items-center h-16 my-6">
                            <CheckCircleOutline style={{fontSize: 64, color: 'green'}} />
                        </div>
                    </FuseAnimate>
                    {/*    <div className="flex flex-col items-center h-16 my-6">*/}
                    <div className="text-base mb-3">
                        Thank you for your purchase!
                    </div>
                    <div className="text-base mb-3">
                        <strong>{minutes} call minutes</strong> have been added
                        to <span className="whitespace-nowrap font-bold">{ausPhone(number)}</span>.
                    </div>
                    <ul className="list-disc mx-4">
                        <li className="pb-3">
                            You have a total of {totalMinutes} call minutes that will expire in <strong>{expiryDays} days</strong>.
                        </li>
                        { state.purchase.cardUpdated  ?
                            <li className="pb-3">Your card has been saved. You can change or remove your saved
                                card on the account page.</li>
                            : null
                        }
                    </ul>

                    <div className="my-2 w-full">
                        <Button aria-label="VIEW RECEIPT" className="w-full" color="primary" variant="outlined"
                                onClick={(e) => externalLink(e, receiptUrl, true)}>
                            VIEW RECEIPT
                        </Button>
                    </div>

                    {/*</div>*/}
                    <div className="mt-4 w-full">
                        <Button variant="contained" color="primary" aria-label="NEXT" className="w-full" component={Link} to="/home">
                            RETURN TO THE DASHBOARD
                        </Button>
                    </div>
                {/*</Card>*/}
            </div>
        </FuseAnimate>
    )
}

const ApiErrorPanel = ({error}) => {

    return (
        <FuseAnimate animation="transition.expandIn" delay={100}>
            <div className="p-4">
                <div className="flex justify-center text-6xl mt-1 mb-4">
                    <SentimentVeryDissatisfied fontSize="inherit" color="error" />
                </div>
                <Typography paragraph>
                    Sorry, it looks like there's a problem at our end. Please try again later.
                </Typography>
                <Typography variant="body2" color="textSecondary">{error.status}/{error.statusText}</Typography>
            </div>
        </FuseAnimate>
    )
}

export default RechargeServicePage;
